/**
 * 동기화 엔진 오케스트레이션 — 결정 #20의 전체 사이클(pull→diff→merge→push).
 *
 * 병합 "판정"은 merge.ts(순수 함수)가, "전송"은 SyncBackend(web 구현)가 맡고,
 * 이 파일은 그 둘을 잇는 **플랫폼 중립 오케스트레이터**다. 로컬 저장소도 직접
 * 만지지 않고 {@link SyncLocal} 추상화를 거치므로, 인메모리 구현으로 전체 흐름을
 * 단위 테스트할 수 있다(전송/IndexedDB 없이).
 *
 * 원칙(결정 #20): 자동 병합 가능한 것만 자동, 진짜 충돌만 onConflict로 사용자에게.
 * 절대 침묵의 데이터 손실 없음.
 */
import type {
  ConflictResolution,
  SyncBackend,
  SyncConflict,
  SyncManifest,
  SyncProgress,
  SyncRecord,
} from "../ports/sync.js";
import { diffManifests, suggestResolution } from "./merge.js";

/**
 * 로컬 저장소 측 동기화 인터페이스. web의 StorageProvider 위에 얇게 구현한다.
 * 오케스트레이터가 플랫폼 중립을 유지하도록 레코드 단위로만 다룬다.
 */
export interface SyncLocal {
  /** 로컬의 현재 매니페스트(id -> hash/rev/deleted). */
  localManifest(): Promise<SyncManifest>;
  /** 직전 동기화 시점의 공통 베이스 매니페스트(없으면 빈 객체). */
  baseManifest(): Promise<SyncManifest>;
  /** 로컬 레코드를 id로 조회(push 대상 직렬화). */
  getRecords(ids: string[]): Promise<SyncRecord[]>;
  /** 원격에서 받은 레코드를 로컬에 적용(merge 결과 반영). */
  applyRecords(records: SyncRecord[]): Promise<void>;
  /** 이번 사이클 종료 후 베이스 매니페스트를 갱신(다음 3-way 기준). */
  saveBaseManifest(manifest: SyncManifest): Promise<void>;

  // ---- 에셋 바이트(콘텐츠 주소화, 결정 #18) — 옵션 ----
  // 구현하면 엔진이 레코드가 참조하는 에셋 바이트까지 "없는 것만" 전송한다.
  // 미구현(인메모리 테스트 등)이면 엔진은 에셋 전송을 건너뛴다.
  /** 로컬에 해당 해시의 blob이 있는지. */
  hasBlob?(hash: string): Promise<boolean>;
  /** 로컬 blob 바이트 조회(push 대상). */
  getBlob?(hash: string): Promise<Blob | undefined>;
  /** 원격에서 받은 blob 바이트를 로컬에 저장(pull 결과). */
  putBlob?(hash: string, blob: Blob): Promise<void>;
}

/** 매니페스트 항목으로부터 SyncManifest 엔트리를 만든다. */
function manifestEntry(rec: SyncRecord): SyncManifest[string] {
  const entry: SyncManifest[string] = { hash: rec.hash, rev: rec.rev };
  if (rec.deleted) entry.deleted = true;
  return entry;
}

/**
 * 전체 동기화 사이클을 실행한다(결정 #20).
 *
 * @param local      로컬 저장소 어댑터.
 * @param backend    원격 백엔드(구글 드라이브 등).
 * @param onConflict 진짜 충돌 시 사용자 선택을 받는 콜백.
 * @param onProgress 진행 상황 콜백(옵션).
 */
export async function runSync(
  local: SyncLocal,
  backend: SyncBackend,
  onConflicts: (conflicts: SyncConflict[]) => Promise<Map<string, ConflictResolution>>,
  onProgress?: (p: SyncProgress) => void,
): Promise<{ pushed: number; pulled: number; conflicts: number }> {
  const progress: SyncProgress = { phase: "manifest", pushed: 0, pulled: 0, conflicts: 0 };
  const report = () => onProgress?.({ ...progress });

  try {
    // 1) 매니페스트 수집(로컬/원격/베이스).
    report();
    const [localM, remoteM, baseM] = await Promise.all([
      local.localManifest(),
      backend.pullManifest(),
      local.baseManifest(),
    ]);

    // 2) diff로 pull/push/conflict 분류(결정 #20).
    const diff = diffManifests(localM, remoteM, baseM);

    // 3) pull: 원격 변경분을 받아 로컬에 적용.
    progress.phase = "pull";
    report();
    if (diff.toPull.length > 0) {
      const remoteRecords = await backend.pullRecords(diff.toPull);
      await pullAssets(remoteRecords, backend, local, progress, report);
      await local.applyRecords(remoteRecords);
      progress.pulled += remoteRecords.length;
    }

    // 4) merge: 충돌을 사용자에게 물어 해소.
    progress.phase = "merge";
    report();
    const pushIds = new Set(diff.toPush);
    if (diff.conflicts.length > 0) {
      const [localRecs, remoteRecs] = await Promise.all([
        local.getRecords(diff.conflicts),
        backend.pullRecords(diff.conflicts),
      ]);
      const localById = new Map(localRecs.map((r) => [r.id, r]));
      const remoteById = new Map(remoteRecs.map((r) => [r.id, r]));

      const conflictsList: SyncConflict[] = [];

      for (const id of diff.conflicts) {
        const l = localById.get(id);
        const r = remoteById.get(id);
        if (!l || !r) {
          // 한쪽이 없으면 충돌 아님 — 존재하는 쪽 채택.
          if (l) pushIds.add(id);
          else if (r) {
            await local.applyRecords([r]);
            progress.pulled += 1;
          }
          continue;
        }
        conflictsList.push({
          table: l.table,
          id,
          local: l,
          remote: r,
          suggestion: suggestResolution(l, r),
        });
      }

      if (conflictsList.length > 0) {
        const resolutions = await onConflicts(conflictsList);

        for (const conflict of conflictsList) {
          const resolution = resolutions.get(conflict.id) ?? conflict.suggestion;
          progress.conflicts += 1;

          if (resolution === "keep-local") {
            pushIds.add(conflict.id);
          } else if (resolution === "keep-remote") {
            await local.applyRecords([conflict.remote]);
            progress.pulled += 1;
          } else {
            // keep-both: 원격을 로컬에 적용하고, 로컬도 새 id로 보존한 뒤 push.
            await local.applyRecords([conflict.remote]);
            progress.pulled += 1;
            pushIds.add(conflict.id); // 호출부가 keep-both 분기를 새 id로 복제했다고 가정
          }
          report();
        }
      }
    }

    // 5) push: 로컬 변경분 + 충돌에서 keep-local 된 것을 원격으로.
    progress.phase = "push";
    report();
    if (pushIds.size > 0) {
      const toPush = await local.getRecords([...pushIds]);
      await pushAssets(toPush, backend, local, progress, report);
      await backend.pushRecords(toPush);
      progress.pushed += toPush.length;
    }

    // 6) 베이스 매니페스트 갱신(다음 3-way 기준). 최신 로컬 매니페스트로.
    const finalManifest = await local.localManifest();
    await local.saveBaseManifest(finalManifest);

    progress.phase = "done";
    report();
    return { pushed: progress.pushed, pulled: progress.pulled, conflicts: progress.conflicts };
  } catch (e) {
    progress.phase = "error";
    report();
    throw e;
  }
}

/** 레코드 payload에서 참조하는 에셋 해시를 수집(콘텐츠 주소화, 결정 #18). */
function collectAssetHashes(records: SyncRecord[]): string[] {
  const hashes = new Set<string>();
  for (const rec of records) {
    // 규약: payload에 assetHashes: string[]가 있으면 그걸 신뢰(없으면 스캔 안 함).
    const p = rec.payload as { assetHashes?: unknown } | null | undefined;
    if (p && Array.isArray(p.assetHashes)) {
      for (const h of p.assetHashes) if (typeof h === "string") hashes.add(h);
    }
  }
  return [...hashes];
}

/** pull한 레코드가 참조하는 에셋 중 로컬에 없는 것만 받는다(결정 #18). */
async function pullAssets(
  records: SyncRecord[],
  backend: SyncBackend,
  local: SyncLocal,
  progress: SyncProgress,
  report: () => void,
): Promise<void> {
  // 로컬이 blob 저장을 지원하지 않으면(인메모리 등) 에셋 전송은 건너뛴다.
  if (!local.putBlob) return;
  const hashes = collectAssetHashes(records);
  if (hashes.length === 0) return;
  progress.phase = "assets";
  report();
  for (const hash of hashes) {
    // 로컬에 이미 있으면 받지 않는다(콘텐츠 주소화 dedup, 결정 #18).
    if (local.hasBlob && (await local.hasBlob(hash))) continue;
    const blob = await backend.pullBlob(hash);
    if (blob) await local.putBlob(hash, blob);
  }
}

/** push할 레코드가 참조하는 에셋 중 원격에 없는 것만 올린다(결정 #18). */
async function pushAssets(
  records: SyncRecord[],
  backend: SyncBackend,
  local: SyncLocal,
  progress: SyncProgress,
  report: () => void,
): Promise<void> {
  // 로컬이 blob 조회를 지원하지 않으면 에셋 전송은 건너뛴다.
  if (!local.getBlob) return;
  const hashes = collectAssetHashes(records);
  if (hashes.length === 0) return;
  progress.phase = "assets";
  report();
  for (const hash of hashes) {
    // 원격에 이미 있으면 올리지 않는다(없는 것만 전송, 결정 #18).
    if (await backend.hasBlob(hash)) continue;
    const blob = await local.getBlob(hash);
    if (blob) await backend.pushBlob(hash, blob);
  }
}

export { manifestEntry };
